import InstanceService from "@/services/InstanceService";
import Idea from "@/types/Idea";
import {deepCopy} from "@/utils/copy";
import Instance from "@/types/Instance";
import {consistantInstance, relationshipValue} from "@/utils/ontosUtils";
import {PendingReverse, withoutPendingIds, createPending} from "@/services/reverseRelationship";
import {
    deleteAt, deleteInstanceMedia, instanceMediaPath, isPending, revoke, takePending, upload
} from "@/services/mediaStorage";
import {isMediaType} from "@/utils/mediaTypes";
import {computingNow} from "@/types/IdeaProperty";


function isEqual(a: any, b: any, unordered = false) {
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        if (!unordered) return a.every((val, index) => val === b[index]);
        const rest = [...b]
        return a.every(val => {
            const i = rest.indexOf(val)
            if (i < 0) return false
            rest.splice(i, 1)
            return true
        });
    }
    return a === b;
}

type MediaAction = { alias: string, type: string, pendingUrl: string | null }

class EditInstanceService {
    private context: any;
    private service: InstanceService;
    private idea: Idea;

    constructor(context: any, service: InstanceService, idea: Idea) {
        this.context = context
        this.service = service
        this.idea = idea
    }

    /**
     * A situação sai separada das demais mudanças, pelo endpoint dedicado — o mesmo
     * que o arrasto de um card do Kanban usa.
     *
     * É ele que carimba o `_stateChangedAt`, de que o prazo dos terminados depende;
     * pelo PATCH o item ficaria com a data velha e sumiria da faixa no recarregamento
     * seguinte. E o PATCH com `_state` nunca foi exercitado: até agora nenhuma tela
     * de edição mostrava o campo.
     */
    async updateChanges(id: number, changes: { [p: string]: any }, target?: Instance) {
        const {_state, ...rest} = changes
        const ref = {
            systemAlias: this.context.system,
            ideaAlias: this.context.idea,
            instanceId: Number(id)
        }
        const actions = this.mediaActions(rest, false)
        const applied = await this.applyMedia(id, actions)
        //Trocar só a situação não tem o que mandar no PATCH, e um corpo vazio seria
        //uma ida ao servidor à toa
        if (Object.keys(rest).length) await this.service.update(ref, {...rest, ...applied})
        if (_state !== undefined) await this.service.updateState(ref, _state)
        this.commitMedia(applied, target)
    }

    /**
     * Exclui o item, e depois os arquivos de mídia dele — só depois de o servidor aceitar, senão
     * ficariam órfãos no bucket para sempre.
     *
     * Aqui, e não em quem pede: a ficha e a lixeira do card no Kanban excluem do mesmo jeito, e a
     * segunda cópia esqueceria os arquivos na primeira vez que alguém a mexesse.
     */
    async delete(id: number) {
        const {system: systemAlias, idea: ideaAlias} = this.context
        await this.service.delete({systemAlias, ideaAlias, instanceId: Number(id)})
        await deleteInstanceMedia(systemAlias, ideaAlias, Number(id), this.idea)
    }

    async update(originalInstance: Instance, modifiedInstance: Instance) {
        const changes = this.instanceDiff(originalInstance, modifiedInstance);
        const id = Number(originalInstance._instance || this.context.instance)
        return this.updateChanges(id, changes, modifiedInstance)
    }

    /**
     * O que fazer com cada propriedade de mídia presente em `values`.
     *
     * Numa edição os valores vêm do diff, então só aparece o que mudou: blob: para
     * arquivo novo, null para removido. Numa criação vêm da instância inteira, onde
     * toda chave existe — daí o onlyPending, sem o qual criar sem escolher arquivo
     * tentaria apagar um que nunca existiu.
     */
    private mediaActions(values: { [p: string]: any }, onlyPending: boolean): MediaAction[] {
        const actions: MediaAction[] = []
        for (const property of this.idea.properties) {
            if (!isMediaType(property) || !(property.alias in values)) continue
            const base = {alias: property.alias, type: property.type as string}
            const value = values[property.alias]
            if (isPending(value)) actions.push({...base, pendingUrl: value})
            else if (!onlyPending && !value) actions.push({...base, pendingUrl: null})
        }
        return actions
    }

    /** Sobe o que está pendente e apaga o que foi removido. Devolve o valor final. */
    private async applyMedia(instanceId: number, actions: MediaAction[]) {
        const applied: { [p: string]: string | null } = {}
        await Promise.all(actions.map(async ({alias, type, pendingUrl}) => {
            const path = instanceMediaPath(this.context.system, this.context.idea, instanceId, alias, type)
            if (!pendingUrl) {
                await deleteAt(path)
                applied[alias] = null
                return
            }
            const blob = takePending(pendingUrl)
            //Sem Blob não há o que subir: a URL não nasceu neste ciclo de edição (um
            //blob: não sobrevive a um reload). Vira null, e não fica de fora — deixar
            //de fora faria o blob: do diff ser gravado como se fosse o valor.
            applied[alias] = blob ? await upload(path, blob) : null
        }))
        return applied
    }

    /**
     * Reescreve o modelo com a URL definitiva e só então revoga o blob:.
     *
     * A ordem importa: antes a substituição acontecia só numa cópia do payload e o
     * objeto do componente ficava para trás. Revogar sem reescrever deixaria o
     * formulário apontando para uma URL morta.
     */
    private commitMedia(applied: { [p: string]: string | null }, target?: Instance) {
        for (const alias of Object.keys(applied)) {
            const previous = target ? target[alias] : null
            if (target) target[alias] = applied[alias]
            revoke(previous)
        }
    }

    /**
     * @param pending instâncias do outro lado de um relacionamento reverso que o
     * usuário montou antes de esta existir. Só podem nascer depois, apontando para
     * o id recém-criado — daí as duas etapas.
     */
    async create(instance: Instance, pending: PendingReverse[] = []) {
        const {system: systemAlias, idea: ideaAlias} = this.context
        const actions = this.mediaActions(instance, true)

        //A imagem não pode ir no POST: o caminho no bucket depende do id, que só
        //existe depois dele. Antes a data URL inteira viajava aqui e estourava o
        //limite de corpo do servidor — era o erro ao criar já escolhendo a imagem.
        const payload = withoutPendingIds(consistantInstance(instance, this.idea), this.idea)
        actions.forEach(({alias}) => {payload[alias] = null})

        const added = await this.service.add({systemAlias, ideaAlias}, payload)
        const id = Number(added._instance)

        if (actions.length) {
            const applied = await this.applyMedia(id, actions)
            //só o que de fato subiu: o POST já mandou null para todos
            const uploaded = Object.keys(applied).filter(alias => applied[alias])
            if (uploaded.length) {
                await this.service.update({systemAlias, ideaAlias, instanceId: id}, applied)
                Object.assign(added, applied)
            }
            this.commitMedia(applied, instance)
        }
        await createPending(this.service, systemAlias, id, pending)
        //Devolve a instância, não só o id: quem abriu o dialog precisa de _instance e
        //_identifier para pôr o recém-criado na lista. O _id é o que a mensagem usa.
        return {...added, _id: id};
    }

    instanceDiff(original: any, modified: any) {
        const originalFlat = deepCopy(original)
        const modifiedFlat = deepCopy(modified)
        //Reverso vem na ordem natural da coleção e o Vuetify põe o novo no fim, então
        //comparar por índice acusaria mudança em toda reabertura. Em relacionamento
        //dono múltiplo a ordem é persistida e significativa, e continua importando.
        //O valor vai pelo relationshipValue, e não cru: era por aqui que um vazio na lista de
        //vínculos chegava ao banco. A regra é a mesma da criação, e mora num lugar só.
        const diff = Object.fromEntries(this.idea.properties
            //Calculada não vai — nem o "Calculado quando" enquanto é calculado: o valor dela é do servidor,
            //e ele recusa a escrita
            .filter(p => !computingNow(modifiedFlat, p) && !isEqual(
                originalFlat[p.alias], modifiedFlat[p.alias],
                !!(p.relationship && p.relationship.reverse)))
            .map(p => [p.alias, p.relationship
                ? relationshipValue(modifiedFlat[p.alias], p) : modifiedFlat[p.alias]]))
        if (originalFlat._state !== modifiedFlat._state) diff._state = modifiedFlat._state
        return diff
    }
}

export default EditInstanceService
